import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {AgentApprovalResponse} from 'graphql-client/__generated/graphqlGlobalTypes';
import ChatUploadedFileChip from 'llm/chat-library/messages/ChatUploadedFileChip';
import {
  ClientOptimisticUserMessage,
  DisplayMessageChatEventNode,
} from 'llm/chat-library/messages/MessageTypes';
import {assertNever} from 'util/Types';

import {ChatMessageBubble} from './ChatMessageBubble';

export interface ChatUserMessageBubbleProps {
  event: DisplayMessageChatEventNode | ClientOptimisticUserMessage;
  chatSessionId: string | null;
}

/**
 * The italic footer note shown inside a user message that answered an approval request, derived
 * directly from the message's own ``agentApprovalDecision``. Returns null for ordinary messages
 * (no decision) so no note is rendered.
 */
function getApprovalIndicatorText(
  event: DisplayMessageChatEventNode | ClientOptimisticUserMessage
): string | null {
  if (event.__typename !== 'DisplayMessageChatEventNode') {
    return null;
  }
  const decision = event.agentApprovalDecision;
  if (decision == null) {
    return null;
  }
  switch (decision.response) {
    case AgentApprovalResponse.APPROVED_THIS_CHAT:
      return 'Approved actions for this chat';
    case AgentApprovalResponse.EXPLICITLY_DENIED:
    case AgentApprovalResponse.IMPLICITLY_DENIED:
      return 'Approval not granted';
    default:
      assertNever(decision.response);
      return null;
  }
}

const useStyles = createStyles((theme) => ({
  chipContainer: {
    marginTop: theme.spacing.sm,
  },
  approvalIndicator: {
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    fontStyle: 'italic',
    opacity: 0.7,
  },
}));

const ChatUserMessageBubble = React.forwardRef<HTMLDivElement, ChatUserMessageBubbleProps>(
  ({event, chatSessionId}, ref): JSX.Element => {
    const {classes} = useStyles();
    const approvalIndicatorText = getApprovalIndicatorText(event);
    return (
      <ChatMessageBubble
        ref={ref}
        variant="user_message"
        toolbar={null}
        faded={event.__typename === 'ClientOptimisticUserMessage'}
        footer={
          approvalIndicatorText != null ? (
            <div
              className={classes.approvalIndicator}
              {...getTestProps({component: 'ChatUserMessageBubble', element: 'approval-indicator'})}
            >
              {approvalIndicatorText}
            </div>
          ) : null
        }
      >
        {event.messageBlocks.flatMap((block, index) => {
          switch (block.__typename) {
            case 'DisplayTextBlockNode': {
              // An approval decision made by clicking Allow/Deny carries no user text; only the styled
              // system indicator (footer) is shown. Skip the empty text block so the bubble isn't
              // padded by an empty line.
              if (block.text === '') {
                return [];
              }
              return [
                <div
                  key={index}
                  {...getTestProps({
                    component: 'ChatUserMessageBubble',
                    element: 'text',
                  })}
                >
                  {block.text}
                </div>,
              ];
            }
            case 'DisplayFileBlockNode': {
              return [
                <div key={index} className={classes.chipContainer}>
                  <ChatUploadedFileChip
                    chatSessionId={chatSessionId}
                    // See: createPendingUserMessage. It produces a ClientOptimisticUserMessage,
                    // where the externalFileId is not set.
                    externalFileId={'externalFileId' in block ? block.externalFileId : null}
                    filename={block.filename}
                    mediaType={block.mediaType}
                  />
                </div>,
              ];
            }
            default: {
              assertNever(block);
              return [];
            }
          }
        })}
      </ChatMessageBubble>
    );
  }
);

export default ChatUserMessageBubble;
